import { useState, useRef, useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { RotateCcw } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useForm, Controller } from "react-hook-form"
import { api, ApiRequestError } from "@/lib/api"
import { cn } from "@/lib/utils"
import type { EmailTemplate } from "@fohet/caravan-booker-shared/types"
import { inputClass, labelClass, sectionClass, primaryBtnClass, sectionHeaderClass } from "@/lib/styleClasses"
import { LocaleTabs, type AppLocale } from "@/components/admin/LocaleTabs"
import { EmailTemplateEditor } from "@/components/editor/EmailTemplateEditor"

export const EMAIL_TEMPLATE_TYPES = ["confirmation", "extension", "payment_confirmed", "payment_reminder", "cancellation", "owner_notification"] as const
export type EmailTemplateType = typeof EMAIL_TEMPLATE_TYPES[number]

const TEMPLATE_VARIABLES: Record<EmailTemplateType, { blocks: string[]; basic: string[] }> = {
    confirmation:       { blocks: ["bookingSummaryBlock", "paymentBlock", "viewReservationButton", "accessCodesBlock", "guidePlannerButton"], basic: ["arealName", "arealAddress", "guestName", "guestEmail", "guestPhone", "licensePlate", "arrival", "departure", "nights", "spotNumber", "total", "viewLink", "bankAccount", "variableSymbol"] },
    extension:          { blocks: ["viewReservationButton"], basic: ["arealName", "arealAddress", "guestName", "arrival", "departure", "nights", "total", "viewLink"] },
    payment_confirmed:  { blocks: ["bookingSummaryBlock", "viewReservationButton", "accessCodesBlock"], basic: ["arealName", "arealAddress", "guestName", "arrival", "departure", "nights", "spotNumber", "total", "viewLink"] },
    payment_reminder:   { blocks: ["bookingSummaryBlock", "paymentBlock", "viewReservationButton"], basic: ["arealName", "arealAddress", "guestName", "arrival", "departure", "nights", "spotNumber", "total", "viewLink", "bankAccount", "variableSymbol", "reminderNumber"] },
    cancellation:       { blocks: ["bookingSummaryBlock"], basic: ["arealName", "arealAddress", "guestName", "arrival", "departure", "nights", "spotNumber", "total"] },
    owner_notification: { blocks: ["bookingSummaryBlock"], basic: ["arealName", "arealAddress", "guestName", "guestEmail", "licensePlate", "arrival", "departure", "nights", "spotNumber", "total"] },
}

const HtmlModeEditor = ({ value, onChange, type }: { value: string; onChange: (v: string) => void; type: EmailTemplateType }) => {
    const { t } = useTranslation()
    const [showBasic, setShowBasic] = useState(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const vars = TEMPLATE_VARIABLES[type]

    const insertVariable = (name: string) => {
        const el = textareaRef.current
        if (!el) return
        const start = el.selectionStart
        const end = el.selectionEnd
        const snippet = `{{${name}}}`
        const next = value.slice(0, start) + snippet + value.slice(end)
        onChange(next)
        requestAnimationFrame(() => {
            el.focus()
            el.setSelectionRange(start + snippet.length, start + snippet.length)
        })
    }

    const chipClass = "cursor-pointer bg-slate-100 hover:bg-brand/20 text-slate-700 text-xs font-mono px-2 py-1 rounded-lg border border-slate-200 transition-colors"

    return (
        <div className="space-y-2">
            {/* Block variables */}
            <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 bg-slate-50 border border-slate-200 rounded-t-2xl">
                <span className="text-xs font-black uppercase tracking-widest text-slate-400 mr-1">{t("admin.settings.emailEditor.availableVariables")}:</span>
                {vars.blocks.map((v) => (
                    <button key={v} type="button" onClick={() => insertVariable(v)} className={chipClass}>{`{{${v}}}`}</button>
                ))}
                <button
                    type="button"
                    onClick={() => setShowBasic((s) => !s)}
                    className="ml-auto text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors"
                >
                    {showBasic ? t("admin.settings.emailEditor.hideBasicVars") : t("admin.settings.emailEditor.showBasicVars")}
                </button>
            </div>
            {/* Basic variables (collapsible) */}
            {showBasic && (
                <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 bg-slate-50 border border-x-slate-200 border-slate-200 border-t-0">
                    {vars.basic.map((v) => (
                        <button key={v} type="button" onClick={() => insertVariable(v)} className={chipClass}>{`{{${v}}}`}</button>
                    ))}
                </div>
            )}
            <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full min-h-[320px] font-mono text-xs bg-white border border-slate-200 rounded-b-2xl p-4 outline-none focus:ring-2 focus:ring-brand resize-y"
                spellCheck={false}
            />
        </div>
    )
}

interface EmailTemplateFormProps {
    arealId: string
    type: EmailTemplateType
    locale: AppLocale
    existingTemplates: EmailTemplate[]
}

const EmailTemplateForm = ({ arealId, type, locale, existingTemplates }: EmailTemplateFormProps) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [editorKey, setEditorKey] = useState(0)
    const [htmlMode, setHtmlMode] = useState(false)
    const sourceRef = useRef<"none" | "default" | "existing">("none")

    const existing = existingTemplates.find((tmpl) => tmpl.type === type && tmpl.locale === locale)

    const { register, handleSubmit, reset, control } = useForm<{ subject: string; bodyHtml: string }>({
        defaultValues: { subject: "", bodyHtml: "" },
    })

    const { data: defaultTemplate } = useQuery({
        queryKey: ["admin", "email-template-default", arealId, type, locale],
        queryFn: () =>
            api.get<{ subject: string; bodyHtml: string }>(
                `/areals/${arealId}/email-templates/defaults/${type}/${locale}`,
            ),
    })

    // Load form: prefer saved template; fall back to system default while it's loading.
    // Re-runs if `existing` arrives after we initialised from the default — saved data wins.
    useEffect(() => {
        if (existing && sourceRef.current !== "existing") {
            sourceRef.current = "existing"
            reset({ subject: existing.subject, bodyHtml: existing.bodyHtml })
            setEditorKey((k) => k + 1)
        } else if (!existing && defaultTemplate && sourceRef.current === "none") {
            sourceRef.current = "default"
            reset({ subject: defaultTemplate.subject, bodyHtml: defaultTemplate.bodyHtml })
            setEditorKey((k) => k + 1)
        }
    }, [existing, defaultTemplate, reset])

    const handleResetToDefault = () => {
        if (!defaultTemplate) return
        reset({ subject: defaultTemplate.subject, bodyHtml: defaultTemplate.bodyHtml })
        setEditorKey((k) => k + 1)
    }

    const saveMutation = useMutation({
        mutationFn: (data: { subject: string; bodyHtml: string }) =>
            api.put<EmailTemplate>(`/areals/${arealId}/email-templates/${type}/${locale}`, {
                type,
                locale,
                subject: data.subject,
                bodyHtml: data.bodyHtml,
            }),
        onSuccess: () => {
            toast.success(t("admin.settings.emailTemplateToastSaved"))
            queryClient.invalidateQueries({ queryKey: ["admin", "email-templates", arealId] })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.emailTemplateToastFailed"))
        },
    })

    const allVars = [...TEMPLATE_VARIABLES[type].blocks, ...TEMPLATE_VARIABLES[type].basic]

    return (
        <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
            <div>
                <label className={labelClass}>{t("admin.settings.emailTemplateSubject")}</label>
                <input {...register("subject")} className={inputClass} placeholder={t("admin.settings.emailTemplateSubject")} />
            </div>
            <div>
                <div className="flex items-center justify-between mb-1">
                    <label className={labelClass}>{t("admin.settings.emailTemplateBody")}</label>
                    <button
                        type="button"
                        onClick={() => setHtmlMode((m) => !m)}
                        className="text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors"
                    >
                        {htmlMode ? t("admin.settings.emailEditor.switchToVisual") : t("admin.settings.emailEditor.switchToHtml")}
                    </button>
                </div>
                <Controller
                    name="bodyHtml"
                    control={control}
                    render={({ field }) => htmlMode
                        ? <HtmlModeEditor value={field.value} onChange={field.onChange} type={type} />
                        : (
                            <EmailTemplateEditor
                                key={`editor-${editorKey}`}
                                value={field.value}
                                onChange={field.onChange}
                                availableVariables={allVars}
                            />
                        )
                    }
                />
            </div>
            <div className="flex gap-3 flex-wrap items-center">
                <button type="submit" disabled={saveMutation.isPending} className={primaryBtnClass}>
                    {saveMutation.isPending ? t("admin.settings.emailTemplateSaving") : t("admin.settings.emailTemplateSave")}
                </button>
                <button
                    type="button"
                    onClick={handleResetToDefault}
                    disabled={!defaultTemplate}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 transition-colors active:scale-95 disabled:opacity-40"
                >
                    <RotateCcw className="size-3.5" />
                    {t("admin.settings.emailTemplateReset")}
                </button>
            </div>
        </form>
    )
}

export const EmailTemplatesSection = ({ arealId }: { arealId: string }) => {
    const { t } = useTranslation()
    const [activeType, setActiveType] = useState<EmailTemplateType>("confirmation")
    const [activeLocale, setActiveLocale] = useState<AppLocale>("cs")

    const { data: templates = [] } = useQuery({
        queryKey: ["admin", "email-templates", arealId],
        queryFn: () => api.get<EmailTemplate[]>(`/areals/${arealId}/email-templates`),
        enabled: !!arealId,
    })

    const typeTabClass = (type: EmailTemplateType) =>
        cn(
            "px-4 py-2 text-sm font-black rounded-2xl transition-colors",
            activeType === type ? "bg-charcoal text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
        )

    return (
        <div className={sectionClass}>
            <h2 className={sectionHeaderClass}>{t("admin.settings.sectionEmailTemplates")}</h2>
            <div className="flex gap-2 flex-wrap">
                {EMAIL_TEMPLATE_TYPES.map((tmplType) => (
                    <button key={tmplType} type="button" onClick={() => setActiveType(tmplType)} className={typeTabClass(tmplType)}>
                        {t(`admin.settings.emailTemplate_${tmplType}`)}
                    </button>
                ))}
            </div>
            <LocaleTabs value={activeLocale} onChange={setActiveLocale} />
            <EmailTemplateForm
                key={`${activeType}-${activeLocale}`}
                arealId={arealId}
                type={activeType}
                locale={activeLocale}
                existingTemplates={templates}
            />
        </div>
    )
}
